<template>
  <div class="vis-wrap" ref="wrapRef">
    <div class="canvas-wrap" ref="canvasWrap"></div>
    <OnlineBasePlot
      v-for="plotInfo in basePlotsInfo"
      :key="plotInfo.id"
      :settings="plotInfo.viewSettings"
      style="position: relative"
      @scaleXChanged="onScaleXChanged($event, plotInfo.id)"
      @scaleYChanged="onScaleYChanged($event, plotInfo.id)"
      @filtersChanged="onFiltersChanged($event, plotInfo.id)"
    />
    <OnlineRhythmPlot
      v-for="plotInfo in rhythmPlotsInfo"
      :key="plotInfo.id"
      :settings="plotInfo.viewSettings"
      style="position: relative"
      @signalsChanged="onRhythmPlotSignalsChanged($event, plotInfo.id)"
      @scaleXChanged="onScaleXChanged($event, plotInfo.id)"
      @scaleYChanged="onScaleYChanged($event, plotInfo.id)"
      @filtersChanged="onFiltersChanged($event, plotInfo.id)"
    />
  </div>
</template>

<script>
import OnlineBasePlot from "./online-base-plot";
import OnlineRhythmPlot from "./online-rhythm-plot";
import { getItemByField } from "../../../web-graphics";

export default {
  name: "OnlineCanvas",
  components: {
    OnlineBasePlot,
    OnlineRhythmPlot
  },
  emits: ["initialized"],
  props: {
    settings: Object
  },
  data: () => ({
    basePlotsInfo: [],
    rhythmPlotsInfo: []
  }),

  watch: {
    settings(newSettings) {
      this.initVisProvider(newSettings);
    }
  },

  methods: {
    initVisProvider(newSettings) {
      if (newSettings === undefined || newSettings === null) {
        return;
      }
      // don't add "visualizatorProvider" to "data" section to avoid wrapping by proxy object
      const visProvider = newSettings.visualizatorProvider;
      visProvider.clearParserCallbacks();
      visProvider.init(newSettings.visSettings, newSettings.saveableVisSettings);
      visProvider.onViewSettingsChanged = plotID => {
        this.updatePlotSettings(plotID);
      };
      visProvider.addParserCallback("beforeSettingsParsing", null, () => {
        visProvider.setCanvasParentHtmlObject(this.$refs.canvasWrap, this.$refs.wrapRef);
      });
      visProvider.create();
      this.visualizatorProvider = visProvider;
      this._afterHtmlCanvasCreation();
    },
    onScaleXChanged(newScaleX, plotId) {
      this.visualizatorProvider.setChannelScaleX(plotId, newScaleX.index, true);
      this.updatePlotSettings(plotId);
    },
    onScaleYChanged(newScaleY, plotId) {
      this.visualizatorProvider.setChannelScaleY(plotId, newScaleY.index, true);
      this.updatePlotSettings(plotId);
    },
    onFiltersChanged(newFiltersNames, plotId) {
      this.visualizatorProvider.setChannelFilters(plotId, newFiltersNames, true);
      this.updatePlotSettings(plotId);
    },
    onRhythmPlotSignalsChanged(signalValue, plotId) {
      let plotInfo = getItemByField("id", plotId, this.visualizatorProvider.plotsInfo);
      plotInfo.changeSourceSignals?.([signalValue.id]);
      this.updatePlotSettings(plotId);
    },
    updatePlotSettings(plotId) {
      // Find plotInfo and extract info
      const { type, id, viewSettings } = this.visualizatorProvider.plotsInfo.find(inf => inf.id === plotId);
      // Get plotInfo collection by type
      let plotList = null;

      let plotType = this.visualizatorProvider.getPlotType(type, ["base", "rhythm"]);
      if (plotType.targetType == "base") {
        plotList = this.basePlotsInfo;
      } else if (plotType.targetType == "rhythm") {
        plotList = this.rhythmPlotsInfo;
      }

      /*
      if (type === "plot:offline:base") {
        plotList = this.basePlotsInfo;
      } else if (type === "plot:offline:rhythm") {
        plotList = this.rhythmPlotsInfo;
      }
      */
      /*
      const plotList =
        type === "plot:offline:base"
          ? this.basePlotsInfo
          : type === "plot:offline:rhythm"
          ? this.rhythmPlotsInfo
          : null;
          */

      // Replace old plotinfo with updated
      const newInfo = {
        id,
        viewSettings: { ...viewSettings }
      };
      const index = plotList.findIndex(p => p.id === plotId);
      plotList[index] = newInfo;
    },
    _afterHtmlCanvasCreation() {
      this.basePlotsInfo = this.visualizatorProvider.getTypedPlotsInfo("base");
      this.rhythmPlotsInfo = this.visualizatorProvider.getTypedPlotsInfo("rhythm");
      this.$nextTick(() => {
        this._finishInitialize();
      });
    },

    _finishInitialize() {
      this.visualizatorProvider.initResizeFunc(this.$refs.wrapRef);
      this.$emit("initialized");
    }
  },
  mounted() {
    this.initVisProvider(this.settings);
  },

  beforeUnmount() {
    if (this.visualizatorProvider !== undefined) {
      this.visualizatorProvider.clear();
    }
  }
};
</script>

<style lang="scss" scoped>
.canvas-wrap {
  height: 100%;
  width: 100%;
  position: absolute;
}
.vis-wrap {
  position: relative;
  height: 100%;
  width: 100%;

  :deep() {
    .controls {
      display: flex;
      flex-direction: row;
      align-items: center;

      .content-wrapper {
        display: flex;
        flex-direction: row;
        align-items: center;
      }

      .select {
        $font-size: 0.8em;
        &-label {
          font-size: $font-size;
        }
        .v-input__control {
          overflow: hidden;
        }

        .v-field {
          &__input {
            height: 28px;
            min-height: initial;
            display: flex;
            align-items: center;
            padding: 0 0 0 5px;
          }

          &__append-inner {
            padding: 2px 0 0 0;
          }
        }

        .v-select {
          &__selection {
            margin-top: 0px;

            &-text {
              font-size: $font-size;
              text-transform: capitalize;
            }
          }
        }
      }
    }
  }
}
</style>
